import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";

import { useJobSourceClients } from "../api";
import { useJobSourceClientsTableColumns } from "../hooks/useJobSourceClientsTableColumns";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/v2/select";
import { DataTable } from "@/components/ui/v2/table/data-table";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { ROUTES } from "@/constants/routes";
import { ModuleName } from "@/features/integrations/utils/module-name";
import { decodeSlug, encodeSlug } from "@/features/integrations/utils/slug";
import { useAuth } from "@/lib/hooks/useAuth";
import { useModuleStatusFilterSearchParams } from "@/lib/hooks/useModuleStatusFilterSearchParams";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";

export default function JobSourceClientsPage() {
  const { t } = useTranslation();
  const { integrationName, clientId } = useParams<{
    integrationName: string;
    clientId?: string;
  }>();

  // All hooks must be called unconditionally before any early returns
  const { tenantId } = useAuth();
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const { moduleStatus: rawModuleStatus, setModuleStatus } =
    useModuleStatusFilterSearchParams();
  const columns = useJobSourceClientsTableColumns();

  // Pass null to API when "all" is selected, otherwise pass the status
  const moduleStatus = rawModuleStatus === "all" ? null : rawModuleStatus;

  const decodedIntegrationName = integrationName
    ? decodeSlug(integrationName)
    : null;
  const backendIntegrationName = decodedIntegrationName ?? "";

  const { data, isLoading, isFetching } = useJobSourceClients({
    tenantId: tenantId ?? "",
    page,
    pageSize,
    integrationName: backendIntegrationName,
    search: debouncedSearchValue,
    moduleStatus: moduleStatus as "active" | "inactive" | null,
  });

  // Redirect to webhooks if accessed in client context (clients page doesn't exist for client context)
  const isClientContext = Boolean(clientId);
  if (isClientContext) {
    const basePath = `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}/${integrationName}/${encodeSlug(ModuleName.JobSource)}`;
    return <Navigate to={`${basePath}/webhooks`} replace />;
  }

  return (
    <div className="space-y-6 mt-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <TableToolbar
          onSearchChange={setSearchValue}
          searchValue={searchValue}
          searchPlaceholder={t("common.search")}
        />
        <Select
          value={rawModuleStatus}
          onValueChange={(value) =>
            setModuleStatus(value as "all" | "active" | "inactive")
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue
              placeholder={t(
                "integrations.cockpit.jobSource.clientsOverview.filterByModuleStatus",
              )}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            <SelectItem value="active">{t("common.active")}</SelectItem>
            <SelectItem value="inactive">{t("common.inactive")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading || isFetching}
        page={page}
        pageSize={pageSize}
        totalRecords={data?.totalRecords ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        getRowId={(item) => item.clientId}
        emptyState={{
          title: debouncedSearchValue
            ? t("common.noResultsFound")
            : t("integrations.cockpit.jobSource.clientsOverview.noClients"),
        }}
      />
    </div>
  );
}
